import { Auth0Provider } from '@auth0/auth0-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { ReactNode, Suspense, lazy, useMemo } from 'react';
import auth0Config from '~/helpers/auth0Config';
import { getCookieValue } from '~/helpers/utils';
import { Auth0AuthProvider } from './Auth0AuthProvider';
import { useShouldUseSvcOnboardingAuth } from './useShouldUseSvcOnboardingAuth';

const CognitoAuthProvider = lazy(() =>
  import('./CognitoAuthProvider').then((m) => ({
    default: m.CognitoAuthProvider,
  }))
);

const SvcOnboardingAuthProvider = lazy(() =>
  import('./SvcOnboardingAuthProvider').then((m) => ({
    default: m.SvcOnboardingAuthProvider,
  }))
);

// The condition is written out (not isMockAuthAllowed()) so the bundler can see it is
// false in production builds and leave the MockAuthProvider chunk out of them
const MockAuthProvider =
  import.meta.env.MODE === 'test' || import.meta.env.MODE === 'development'
    ? lazy(() =>
        import('./MockAuthProvider').then((m) => ({
          default: m.MockAuthProvider,
        }))
      )
    : null;

export { CognitoAuthEvent, useAuth } from './AuthContext';

// Checked again at render time because tests change MODE after this module has loaded
const isMockAuthAllowed = (): boolean =>
  import.meta.env.MODE === 'test' || import.meta.env.MODE === 'development';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const isInternalProd = import.meta.env.MODE === 'internal-prod';
  const shouldUseCognito =
    useFeatureFlagEnabled('cognito-authentication') && !isInternalProd;

  const shouldUseSvcOnboarding = useShouldUseSvcOnboardingAuth();

  const hasIsAuthenticatedCookie = useMemo(() => {
    const auth0IsAuthenticatedCookie = getCookieValue(
      `auth0.${import.meta.env.VITE_AUTH0_CLIENT_ID}.is.authenticated`
    );

    return auth0IsAuthenticatedCookie === 'true';
  }, []);

  if (
    MockAuthProvider &&
    isMockAuthAllowed() &&
    new URLSearchParams(window.location.search).has('mockAuth')
  ) {
    return (
      <Suspense>
        <MockAuthProvider>{children}</MockAuthProvider>
      </Suspense>
    );
  }

  if (shouldUseSvcOnboarding) {
    return (
      <Suspense>
        <SvcOnboardingAuthProvider>{children}</SvcOnboardingAuthProvider>
      </Suspense>
    );
  }

  if (shouldUseCognito) {
    return (
      <Suspense>
        <CognitoAuthProvider>{children}</CognitoAuthProvider>
      </Suspense>
    );
  }

  return (
    <Auth0Provider {...auth0Config}>
      <Auth0AuthProvider hasIsAuthenticatedCookie={hasIsAuthenticatedCookie}>
        {children}
      </Auth0AuthProvider>
    </Auth0Provider>
  );
};
